import { CollectionAdapter } from "../../main/adapters/CollectionAdapter/CollectionAdapter";
import { HttpAdapter } from "../../main/adapters/HttpAdapter";
import { MissingParamError } from "../../utils/erros/erros";

const Users = new Mongo.Collection("Users", {
    connection: null
});

export class User extends CollectionAdapter {
    HttpAdapter = new HttpAdapter();
    baseUrl = `${publicSettings.aws.baseUrls.usersManagement}/users`;

    constructor({ Accounting } = {}) {
        super(Users);
        this.Accounting = Accounting;
    }

    async authenticate(email, password) {
        if (!email || !password)
            throw new MissingParamError("email or password");
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/authenticate`,
                {
                    email,
                    password
                }
            );

            return response;
        } catch (error) {
            throw new Error(`Error authenticating: ${error.message}`);
        }
    }

    async refreshToken(refreshToken) {
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/refresh-tokens`,
                {
                    refreshToken
                }
            );
            return response;
        } catch (error) {
            throw new Error(`Error refreshing token: ${error.message}`);
        }
    }

    async getByUsername({ username, options = {} }) {
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/get-by-username`,
                {
                    username,
                    options
                }
            );
            return response;
        } catch (error) {
            throw new Error(error?.message || "Erro ao buscar usuário");
        }
    }

    async getUsersByRolesAndAccountingId({
        accountingId,
        roles,
        options = {}
    }) {
        if (!accountingId) throw new MissingParamError("accountingId");
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/get-users-by-roles-and-accountingid`,
                {
                    accountingId,
                    roles,
                    options
                }
            );
            return response;
        } catch (error) {
            throw new Error(error);
        }
    }

    _transformRole(role) {
        const roles = {
            Contabilidade: "Administrador",
            Empresa: "Empresa",
            Contabil: "Contábil",
            Comercial: "Comercial",
            Fiscal: "Fiscal",
            Pessoal: "Pessoal",
            Financeiro: "Financeiro",
            Gestor: "Gestor",
            "Pessoal e Fiscal": "Pessoal e Fiscal",
            ContabilidadeTrial: "Trial",
            ContabilidadeTrialPro: "Trial"
        };

        return roles[role];
    }

    /**
     * @public
     * Verifica se usúario tem permissão baseado das regras
     * @param {Object} data { roles: [], granted: [] }
     * @returns {Boolean}
     * @description Recebe as roles do usuário e as roles que terão permissão
     */
    checkPermission({ roles, granted }) {
        if (!roles) throw new MissingParamError("roles");

        if (!granted) throw new MissingParamError("granted");

        return granted.some((role) => roles.includes(role));
    }

    async setPassword(_id, newPassword) {
        await this.HttpAdapter.call(`${this.baseUrl}/change-password`, {
            _id,
            password: newPassword
        });
    }

    async createApiUser(name, accountingId) {
        if (!name) throw new MissingParamError("name");
        if (!accountingId) throw new MissingParamError("accountingId");
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/create-api`,
                {
                    name,
                    accountingId
                }
            );
            return response;
        } catch (error) {
            throw new Error(error);
        }
    }

    async saveMany(accountingId, users) {
        if (!accountingId) throw new MissingParamError("accountingId");
        if (!users) throw new MissingParamError("users");
        const response = await this.HttpAdapter.call(
            `${this.baseUrl}/save-many`,
            {
                accountingId,
                users
            }
        );
        return response;
    }

    async authToken(liveToken) {
        if (!liveToken) return null;
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/authenticate-token`,
                {
                    token: liveToken
                }
            );
            return response;
        } catch (error) {
            throw new Error(error?.message || "Erro ao autenticar token");
        }
    }

    async getByIds(ids) {
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/get-by-ids`,
                {
                    ids
                }
            );
            const list = response?.list || [];
            return list.map((user) => {
                const profile = user.profile || {};
                return {
                    ...user,
                    profile: {
                        ...profile,
                        name: user.name,
                        anonymous: user.isAnonimo,
                        isAnonimo: user.isAnonimo
                    },
                    roles: [user.role]
                };
            });
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    async getByIdsConfigs(ids, configs = {}) {
        if (!Array.isArray(ids)) return null;
        if (!ids.length) return null;
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/get-by-ids`,
                {
                    ids
                },
                configs
            );
            return response;
        } catch (error) {
            throw new Error(error?.message || "Erro ao buscar usuários");
        }
    }

    async getSignUpData({ _id, accountingId }) {
        if (!_id) return null;
        if (!accountingId) return null;
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/get-sign-up-data`,
                {
                    _id,
                    accountingId
                }
            );
            return response;
        } catch (error) {
            throw new Error(
                error?.message || "Erro ao buscar dados de inscrição"
            );
        }
    }

    async completeSignUp({ _id, accountingId, password }) {
        if (!_id) return null;
        if (!accountingId) return null;
        if (!password) return null;
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/complete-sign-up`,
                {
                    _id,
                    accountingId,
                    password
                }
            );
            return response;
        } catch (error) {
            throw new Error(error?.message || "Erro ao completar inscrição");
        }
    }

    async releaseBeta(accountingId) {
        if (!accountingId) throw new MissingParamError("accountingId");

        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/release-beta`,
                {
                    accountingId
                }
            );
            return response;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    async passwordRecover(email, options = {}) {
        if (!email) return null;
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrl}/recovery-password`,
                {
                    email,
                    options
                }
            );
            return response;
        } catch (error) {
            throw new Error(error?.message || "Erro ao recuperar senha");
        }
    }

    async removeByIds(ids) {
        if (!Array.isArray(ids)) return null;
        if (!ids.length) return null;
        const response = await this.HttpAdapter.call(
            `${this.baseUrl}/remove-by-ids`,
            {
                ids
            }
        );
        return response;
    }
}
